export type DeviceChoice = {
  deviceId: string;
  label: string;
};

const CAM_KEY = 'pulse.cameraDeviceId';
const MIC_KEY = 'pulse.micDeviceId';

export function readSavedCameraId(): string | null {
  try {
    return localStorage.getItem(CAM_KEY);
  } catch {
    return null;
  }
}

export function readSavedMicId(): string | null {
  try {
    return localStorage.getItem(MIC_KEY);
  } catch {
    return null;
  }
}

export function saveCameraId(id: string) {
  try {
    localStorage.setItem(CAM_KEY, id);
  } catch {
    /* ignore */
  }
}

export function saveMicId(id: string) {
  try {
    localStorage.setItem(MIC_KEY, id);
  } catch {
    /* ignore */
  }
}

export function clearSavedDeviceIds() {
  try {
    localStorage.removeItem(CAM_KEY);
    localStorage.removeItem(MIC_KEY);
  } catch {
    /* ignore */
  }
}

export async function listMediaDevices(): Promise<{
  cameras: DeviceChoice[];
  mics: DeviceChoice[];
}> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const cameras: DeviceChoice[] = [];
  const mics: DeviceChoice[] = [];
  let camIdx = 0;
  let micIdx = 0;
  for (const d of devices) {
    if (d.kind === 'videoinput') {
      camIdx += 1;
      cameras.push({
        deviceId: d.deviceId,
        label: d.label || `Camera ${camIdx}`,
      });
    } else if (d.kind === 'audioinput') {
      micIdx += 1;
      mics.push({
        deviceId: d.deviceId,
        label: d.label || `Microphone ${micIdx}`,
      });
    }
  }
  return { cameras, mics };
}

/** Human-readable reason for haircheck / preview failures. */
export function formatMediaError(err: unknown): string {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'Camera/microphone permission is required to join.';
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'No camera or microphone was found.';
      case 'NotReadableError':
      case 'TrackStartError':
        return 'Camera or microphone is already in use by another app.';
      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        return 'Saved camera/mic is unavailable — cleared; reload and try again.';
      case 'SecurityError':
        return 'Camera/microphone blocked by the browser (secure context or policy).';
      case 'AbortError':
        return 'Camera/microphone start was interrupted — reload and try again.';
      default:
        return `Camera/microphone could not be started (${err.name}).`;
    }
  }
  const msg = err instanceof Error ? err.message : String(err || 'unknown');
  return `Camera/microphone could not be started (${msg.slice(0, 80)}).`;
}

type GumConstraints = MediaStreamConstraints;

/** Prefer browser AEC so remote LiveKit/TTS playout is cancelled from the mic. */
const MIC_AUDIO: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

function withMicAudio(
  audio: boolean | MediaTrackConstraints | undefined,
): boolean | MediaTrackConstraints {
  if (audio === false) return false;
  if (audio === true || audio === undefined) return { ...MIC_AUDIO };
  return { ...MIC_AUDIO, ...audio };
}

async function gum(constraints: GumConstraints): Promise<MediaStream> {
  const next: GumConstraints = {
    ...constraints,
    audio: withMicAudio(constraints.audio as boolean | MediaTrackConstraints | undefined),
  };
  return navigator.mediaDevices.getUserMedia(next);
}

/**
 * Open a haircheck preview stream. Tries preferred device ids, then defaults,
 * then audio-only / video-only so a single broken device does not hard-block.
 */
export async function acquirePreviewStream(opts: {
  cameraId?: string | null;
  micId?: string | null;
  video?: boolean;
  audio?: boolean;
}): Promise<MediaStream> {
  const wantVideo = opts.video !== false;
  const wantAudio = opts.audio !== false;
  const attempts: GumConstraints[] = [];

  if (wantVideo && wantAudio && (opts.cameraId || opts.micId)) {
    attempts.push({
      video: opts.cameraId
        ? { deviceId: { exact: opts.cameraId } }
        : { facingMode: 'user' },
      audio: opts.micId ? { deviceId: { exact: opts.micId } } : true,
    });
  }
  if (wantVideo && wantAudio) {
    attempts.push({ video: { facingMode: 'user' }, audio: true });
    attempts.push({ video: true, audio: true });
  }
  if (wantAudio) attempts.push({ video: false, audio: true });
  if (wantVideo) {
    attempts.push({ video: { facingMode: 'user' }, audio: false });
    attempts.push({ video: true, audio: false });
  }

  let lastErr: unknown;
  for (const constraints of attempts) {
    try {
      return await gum(constraints);
    } catch (e) {
      lastErr = e;
      if (
        e instanceof DOMException &&
        (e.name === 'OverconstrainedError' || e.name === 'ConstraintNotSatisfiedError')
      ) {
        clearSavedDeviceIds();
      }
      if (
        e instanceof DOMException &&
        (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')
      ) {
        throw e;
      }
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('getUserMedia failed');
}

/** Stop and remove all video tracks so the OS camera LED can turn off (haircheck camera off). */
export function releaseVideoTracks(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getVideoTracks()) {
    stream.removeTrack(track);
    track.stop();
  }
}

/** Add a live camera track to an existing stream (audio-only haircheck → camera on). */
export async function attachVideoTrack(
  stream: MediaStream,
  cameraId?: string | null,
): Promise<void> {
  if (stream.getVideoTracks().some((t) => t.readyState === 'live')) return;
  const fresh = await acquirePreviewStream({
    cameraId,
    video: true,
    audio: false,
  });
  const video = fresh.getVideoTracks()[0];
  if (!video) {
    fresh.getTracks().forEach((t) => t.stop());
    throw new Error('No video track on selected camera');
  }
  stream.addTrack(video);
  for (const t of fresh.getTracks()) {
    if (t !== video) {
      fresh.removeTrack(t);
      t.stop();
    }
  }
}

export async function replaceTrackDevice(
  stream: MediaStream,
  kind: 'video' | 'audio',
  deviceId: string,
): Promise<MediaStream> {
  const constraints =
    kind === 'video'
      ? { video: { deviceId: { exact: deviceId } }, audio: false }
      : { audio: { deviceId: { exact: deviceId } }, video: false };
  const fresh = await navigator.mediaDevices.getUserMedia(constraints);
  const newTrack = kind === 'video' ? fresh.getVideoTracks()[0] : fresh.getAudioTracks()[0];
  if (!newTrack) {
    fresh.getTracks().forEach((t) => t.stop());
    throw new Error(`No ${kind} track on selected device`);
  }
  const oldTracks = kind === 'video' ? stream.getVideoTracks() : stream.getAudioTracks();
  for (const t of oldTracks) {
    stream.removeTrack(t);
    t.stop();
  }
  stream.addTrack(newTrack);
  // Stop unused opposite-kind tracks from the temporary getUserMedia call
  for (const t of fresh.getTracks()) {
    if (t !== newTrack) t.stop();
  }
  return stream;
}
